import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';
import GroupCard from './components/GroupCard';
import MyGroupsCarousel from './components/MyGroupsCarousel';
import CreateGroupModal from './components/CreateGroupModal';
import AuthModal from '../../components/feature/AuthModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface Group {
  id: string;
  sequential_id: number;
  name: string;
  description: string;
  cover_image: string;
  category: string;
  privacy: string;
  member_count: number;
  created_at: string;
  is_featured: boolean;
  is_trending: boolean;
}

const GroupsPage = () => {
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [userMemberships, setUserMemberships] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewType, setViewType] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'alphabetical'>('popular');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const categories = ['All', 'Entertainment', 'Production', 'Genre', 'Regional', 'Professional'];

  const handleCreateGroupClick = () => {
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }
    setIsCreateModalOpen(true);
  };

  useEffect(() => {
    loadGroups();
  }, [user, refreshKey]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('groups')
        .select('id, sequential_id, name, description, cover_image, category, privacy, member_count, created_at, is_featured, is_trending')
        .order('member_count', { ascending: false });

      if (error) {
        console.error('Error loading groups:', error);
        setGroups([]);
      } else {
        setGroups(data || []);
      }

      // Load user memberships
      if (user) {
        const { data: memberships } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id);
        
        if (memberships) {
          const membershipSet = new Set(memberships.map(m => m.group_id));
          console.log('Loaded user memberships:', membershipSet.size, 'groups');
          setUserMemberships(membershipSet);
        }
      } else {
        setUserMemberships(new Set());
      }
    } catch (err) {
      console.error('Error:', err);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMembershipChange = async (groupId: string, newIsMember: boolean) => {
    console.log('Membership changed:', { groupId, newIsMember });
    
    // Update local membership state immediately
    setUserMemberships(prev => {
      const next = new Set(prev);
      if (newIsMember) {
        next.add(groupId);
      } else {
        next.delete(groupId);
      }
      return next;
    });

    // Update member count in groups list
    setGroups(prev =>
      prev.map(g =>
        g.id === groupId
          ? { ...g, member_count: newIsMember ? g.member_count + 1 : Math.max(0, g.member_count - 1) }
          : g
      )
    );

    // Trigger a refresh to ensure all data is in sync
    setTimeout(() => setRefreshKey(k => k + 1), 500);
  };

  const filteredGroups = groups
    .filter((group) => {
      const matchesSearch =
        group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || group.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'alphabetical':
          return a.name.localeCompare(b.name);
        case 'popular':
        default:
          return b.member_count - a.member_count;
      }
    });

  const featuredGroups = groups.filter((g) => g.is_featured);
  const trendingGroups = groups.filter((g) => g.is_trending);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Community Groups</h1>
            <p className="text-gray-600">Join groups to connect with fellow AI drama enthusiasts</p>
          </div>
          <button
            onClick={handleCreateGroupClick}
            className="mt-4 md:mt-0 px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Create Group
          </button>
        </div>

        {/* My Groups Carousel */}
        {user && (
          <MyGroupsCarousel
            key={`my-groups-${refreshKey}`}
            userMemberships={userMemberships}
            onMembershipChange={loadGroups}
          />
        )}

        {/* Featured Groups */}
        {featuredGroups.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-star-line text-amber-500"></i>
              Featured Groups
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredGroups.slice(0, 3).map((group) => (
                <GroupCard 
                  key={`featured-${group.id}-${refreshKey}`}
                  group={group} 
                  viewType="grid" 
                  isMember={userMemberships.has(group.id)}
                  onMembershipChange={(newIsMember) => handleMembershipChange(group.id, newIsMember)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Trending Groups */}
        {trendingGroups.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-fire-line text-orange-500"></i>
              Trending Groups
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {trendingGroups.slice(0, 4).map((group) => (
                <GroupCard 
                  key={`trending-${group.id}-${refreshKey}`}
                  group={group} 
                  viewType="grid"
                  isMember={userMemberships.has(group.id)}
                  onMembershipChange={(newIsMember) => handleMembershipChange(group.id, newIsMember)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search groups..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Sort & View */}
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                <option value="popular">Most Popular</option>
                <option value="newest">Newest</option>
                <option value="alphabetical">A-Z</option>
              </select>

              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewType('grid')}
                  className={`p-2 rounded-md transition-colors cursor-pointer ${
                    viewType === 'grid' ? 'bg-white shadow-sm text-teal-600' : 'text-gray-500'
                  }`}
                >
                  <i className="ri-grid-fill"></i>
                </button>
                <button
                  onClick={() => setViewType('list')}
                  className={`p-2 rounded-md transition-colors cursor-pointer ${
                    viewType === 'list' ? 'bg-white shadow-sm text-teal-600' : 'text-gray-500'
                  }`}
                >
                  <i className="ri-list-check"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Groups List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading groups...</p>
            </div>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-group-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No groups found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || selectedCategory !== 'All'
                ? 'Try adjusting your search or filters'
                : 'Be the first to create a group!'}
            </p>
            <button
              onClick={handleCreateGroupClick}
              className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Create Group
            </button>
          </div>
        ) : (
          <div
            className={
              viewType === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }
          >
            {filteredGroups.map((group) => (
              <GroupCard 
                key={`all-${group.id}-${refreshKey}`}
                group={group} 
                viewType={viewType}
                isMember={userMemberships.has(group.id)}
                onMembershipChange={(newIsMember) => handleMembershipChange(group.id, newIsMember)}
              />
            ))}
          </div>
        )}
      </div>

      <Footer />

      <CreateGroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={loadGroups}
      />

      {/* Auth Modal for login prompt */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialMode="signin"
        message="Please sign in to create a group"
      />
    </div>
  );
};

export default GroupsPage;
